/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { createSelector } from '@reduxjs/toolkit'
import { IRootState } from 'store'
import moment from 'moment'
import { groupBy, uniqBy } from 'lodash'

export {
  COL,
  type ColumnName,
  policyNameAt,
  policyCodeAt,
  policyInsurerAt,
  policyWordingPathAt,
  policyContentAt,
  policyIconAt,
  policyPageAt,
} from 'scenes/comparison/components/csv-export/csv-constants'
import {
  CategoryFilter,
  IPolicy,
  ISectionFeature,
  ISectionFeatures,
  IGroupedFeaturesBySection,
  IGroupedPoliciesByInsurer,
  ClassLines,
  IClassLine,
  IBuyGroup,
  IFeatureContent,
  IFeatureContents,
  CompareBy,
} from '../../types'
import { shouldHideComparisonRow } from 'scenes/comparison/comparison-row-visibility'

const mediaUrl = process.env.REACT_APP_MEDIA_BASE_URL

// Removed column helpers moved to csv-constants

// Previously used to translate icon labels to wording; retained for reference only

const getFilteredFeatures = createSelector(
  (state: IRootState) => state.comparison.comparisonFeatures,
  (state: IRootState) => state.comparison.filter,
  (state: IRootState) => state.comparison.comparisonOptions,
  (comparisonFeatures, filter) => {
    const filterFeaturesArr = Object.values(filter?.filterFeature).flat()

    const filteredfeatures = comparisonFeatures
      .map((feature) => {
        if (!feature?.features) return null
        let filtered = feature.features

        if (filterFeaturesArr.length > 0) {
          filtered = filtered.filter((f) => filterFeaturesArr.includes(f.id))
        }

        switch (filter.category) {
          case CategoryFilter.MOST_POPULAR:
            filtered = filtered.filter((f) => f.is_most_popular)
            break
          case CategoryFilter.WATER_PERILS:
            filtered = filtered.filter((f) => f.is_water_perils)
            break
          case CategoryFilter.KEY_SELLING_FEATURES:
            filtered = filtered.filter((f) => f.is_key_selling_feature)
            break
          case CategoryFilter.TRAINING:
            filtered = filtered.filter((f) => f.is_training)
            break
          // CategoryFilter.ALL or default: no further filtering
        }

        return { ...feature, features: filtered }
      })
      .filter(Boolean)

    return filteredfeatures
  },
)

export const getGroupedFeaturesBySection = createSelector(
  (state: IRootState) => state.comparison.comparisonFeatures,
  (comparisonFeatures: ISectionFeatures[]) => {
    const groupedSectionsByCover = comparisonFeatures.map(({ features, cover_name, cover_id }) => {
      const grouped: { [any: string]: ISectionFeature[] } = groupBy(
        features,
        (feature) => feature.section,
      )
      const groupedFeaturesBySection: IGroupedFeaturesBySection[] = Object.entries(grouped)
        .map(([key, value]) => ({
          section: key,
          seq: value[0].seq,
          features: value,
        }))
        .sort((a, b) => a.seq - b.seq)

      return { cover_id, cover_name, sections: groupedFeaturesBySection }
    })

    return groupedSectionsByCover
  },
)

export const getFilteredGroupedFeaturesBySection = createSelector(
  getFilteredFeatures,
  (filteredFeatures: ISectionFeatures[]) => {
    const groupedSectionsByCover = filteredFeatures.map(({ features, cover_name, cover_id }) => {
      const grouped: { [any: string]: ISectionFeature[] } = groupBy(
        features,
        (feature) => feature.section,
      )
      const groupedFeaturesBySection: IGroupedFeaturesBySection[] = Object.entries(grouped)
        .map(([key, value]) => ({
          section: key,
          seq: value[0].seq,
          features: value,
        }))
        .sort((a, b) => a.seq - b.seq)

      return { cover_id, cover_name, sections: groupedFeaturesBySection }
    })

    return groupedSectionsByCover
  },
)

const getFilteredPolicies = createSelector(
  (state: IRootState) => state.comparison.comparisonPolicies,
  (state: IRootState) => state.comparison.filter,
  (comparisonPolicies, filter) => {
    const policies = comparisonPolicies ? [...comparisonPolicies] : []
    const filteredPoliciesArr = Object.values(filter?.filterPolicy).flat()
    let filtered = [...policies]

    if (filteredPoliciesArr.length > 0) {
      filtered = filtered.filter((policy) => filteredPoliciesArr.includes(policy.id))
    }

    if (filter?.effectiveDate) {
      filtered = filtered.filter(({ start_date, end_date }) => {
        return (
          moment(start_date).isBefore(moment(filter?.effectiveDate)) &&
          moment(end_date).add(12, 'months').isAfter(moment(filter?.effectiveDate))
        )
      })
    }

    return filtered
  },
)

export const getGroupedPoliciesByInsurer = createSelector(
  (state: IRootState) => state.comparison.comparisonPolicies,
  (state: IRootState) => state.auth?.account,
  (state: IRootState) => state.comparison.filter.showExpiredInPicker,
  (policies, account, showExpiredInPicker) => {
    const grouped: { [any: string]: IPolicy[] } = groupBy(
      policies,
      (policies) => policies.insurer?.name,
    )

    const normalizeName = (name: string) => name
    // ? name
    //     .replace(/\s?\(PDS\)/gi, '')
    //     .trim()
    //     .toLowerCase()
    // : ''

    const groupedPoliciesByInsurer: IGroupedPoliciesByInsurer[] = Object.entries(grouped).map(
      ([key, value]) => {
        // Group by normalized policy name, insurer ID, and underwriter ID
        const policiesByNameAndCompany: { [key: string]: IPolicy[] } = groupBy(
          value,
          (policy: IPolicy) =>
            `${normalizeName(policy.name)}|${policy.insurer?.id}|${policy.underwriter?.id}`,
        )

        // For each group, pick all non-expired policies and the most recently expired policy (if any)
        const policyList = Object.values(policiesByNameAndCompany).map((policie: IPolicy[]) => {
          const expiredPolicies = policie.filter((p) => p.is_expired)
          const currentPolicies = policie.filter((p) => !p.is_expired)

          const selected: IPolicy[] = []

          if (showExpiredInPicker && expiredPolicies.length > 0) {
            const mostRecentExpired = expiredPolicies.reduce((latest, current) =>
              moment(current.end_date).isAfter(moment(latest.end_date)) ? current : latest,
            )
            selected.push(mostRecentExpired)
          }

          selected.push(...currentPolicies)
          return selected
        })

        const flatPolicies = policyList.flat()

        return {
          insurer: key,
          insurerLogo: value[0]?.insurer?.logo,
          insurerWebsite: value[0]?.insurer?.website,
          rating: value[0]?.underwriter?.csr,
          policies: flatPolicies,
          requireApproval: flatPolicies.some(
            (policy) =>
              Array.isArray(policy.sections) &&
              policy.sections.some((section) => !section.is_approved),
          ),
        }
      },
    )

    // This is for a viewing room user, policies need approval to be first in the list
    if (account?.is_viewing_room) {
      return groupedPoliciesByInsurer.sort((a) => (a.requireApproval ? -1 : 1))
    }

    return groupedPoliciesByInsurer.sort((a, b) => (a.insurer < b.insurer ? -1 : 1))
  },
)

export const getFilteredGroupedPoliciesByInsurer = createSelector(
  getFilteredPolicies,
  (state: IRootState) => state.auth?.account,
  (policies, account) => {
    const grouped: { [any: string]: IPolicy[] } = groupBy(
      policies,
      (policies) => policies.insurer?.name,
    )
    const groupedPoliciesByInsurer: IGroupedPoliciesByInsurer[] = Object.entries(grouped).map(
      ([key, value]) => ({
        insurer: key,
        insurerLogo: value[0].insurer.logo,
        insurerWebsite: value[0]?.insurer?.website,
        rating: value[0]?.underwriter?.csr,
        policies: value,
      }),
    )
    // This is for a viewing room user, policies need approval to be first in the list
    if (account?.is_viewing_room) {
      return groupedPoliciesByInsurer.sort((a) => (a.requireApproval ? -1 : 1))
    }

    return groupedPoliciesByInsurer
  },
)

export const getClassCategories = createSelector(
  (state: IRootState) => state?.comparison?.productClasses,
  (classLines) => {
    const allPackageSecionExcluded = (classLines ?? []).map((classLine) => ({
      ...classLine,
      classes: classLine.classes.map((cl) => ({
        ...cl,
        covers: cl?.covers.filter((cover) => !cover?.name.includes(ClassLines.ALL_PACKAGE_SECTION)),
      })),
    }))
    return allPackageSecionExcluded
  },
)

export const getAllCovers = createSelector(
  (state: IRootState) => state?.comparison?.productClasses,
  (classLines) => {
    const classes = (classLines ?? []).map((classLine) =>
      classLine.classes.map((cl) =>
        cl?.covers.filter((cover) => !cover?.name.includes(ClassLines.ALL_PACKAGE_SECTION)),
      ),
    )
    const allCoverIds: number[] = classes
      ?.flat()
      ?.flat()
      .map((c) => c.id)
    return allCoverIds
  },
)

const getSectionFeaturesContents = createSelector(
  getFilteredGroupedFeaturesBySection,
  (state: IRootState) => state.comparison.selectedPolicyContents,
  (state: IRootState) => state.comparison.filter,
  (coverSections, selectedPolicyContents, filter) => {
    const selectedPolicies = selectedPolicyContents.map((content) => content.policy).filter(Boolean)
    return coverSections.map((cover) => ({
      ...cover,
      sections: cover.sections
        .map((coverFeature) => ({
          ...coverFeature,
          features: coverFeature.features
            .map((feature) => {
              // Build contents array for this feature across all selected policies
              const contents = selectedPolicyContents.map((content) => {
                const featureContent = content.featureContents.find(
                  (fc) => fc.feature_id === feature.id,
                )
                return featureContent ? { ...featureContent, id: content.id } : { id: content.id }
              })
              return { ...feature, contents }
            })
            .filter(
              (feature) =>
                !shouldHideComparisonRow(
                  filter,
                  CompareBy.COMPANY,
                  feature.contents || [],
                  selectedPolicies,
                  undefined,
                ),
            ),
        }))
        .filter((coverFeature) => coverFeature.features.length),
    }))
  },
)

/**
 * Feature IDs to scope PDF/Excel export to, in COMPANY mode.
 *
 * Smart row filters (differences / what's new / limits) narrow the visible rows but must never
 * be written back into `filter.filterFeature` — that field holds the user's own picks, and
 * overwriting it strands a stale whitelist once the smart filter is switched off. The scope is
 * derived here instead, off the same rows the table renders.
 *
 * Returns `[]` when nothing is narrowing the comparison, which the export API reads as "all".
 */
const getExportFeatureIds = createSelector(
  getSectionFeaturesContents,
  (state: IRootState) => state.comparison.filter,
  (coverSections, filter) => {
    const hasManualSelection = Object.values(filter.filterFeature).flat().length > 0
    const hasSmartFilter = filter.showDifferences || filter.onlyWhatsNew || filter.showLimitsOnly

    if (!hasManualSelection && !hasSmartFilter) return []

    return coverSections
      .map((cover) => cover.sections.map((section) => section.features.map((f) => f.id)))
      .flat(2)
  },
)

export const comparisonSelectors = {
  getSectionFeaturesContents,
  getExportFeatureIds,
  getSelectedPolicies: createSelector(
    (state: IRootState) => state.comparison.selectedPolicyContents,
    (selectedPolicyContents) => {
      return selectedPolicyContents.map((policy) => policy.policy)
    },
  ),
  getSelectedSectionFeatures: createSelector(
    (state: IRootState) => state.comparison.comparisonFeatures,
    (state: IRootState) => state.comparison.selectedSectionCovers,
    (comparisonFeatures, selectedSectionCovers) => {
      const foundFeatures: ISectionFeature[] = []
      selectedSectionCovers.forEach((selectedFeature) => {
        comparisonFeatures?.forEach(({ features }: { features: ISectionFeature[] }) => {
          const foundFeature = features.find((feature) => feature.id === selectedFeature.id)
          if (foundFeature) foundFeatures.push(foundFeature)
        })
      })
      return foundFeatures
    },
  ),
  getPolicyFeaturesContents: createSelector(
    getFilteredGroupedPoliciesByInsurer,
    (state: IRootState) => state.comparison.selectedSectionCovers,
    (state: IRootState) => state.comparison.filter,
    (insurerPolicies, selectedSectionCovers, filter) => {
      return insurerPolicies.map((insurer) => {
        const policiesWithContents: IPolicy[] = insurer.policies
          .map((policy) => {
            const contents = selectedSectionCovers.map((cover) => {
              // Find the featureContent for this policy and cover
              const featureContent = cover.featureContents.find(
                (fc) => fc.policy_proxy_id === policy.proxy_id,
              )
              // Cells with no content for this policy stay sparse — consumers read them as
              // Partial<IFeatureContent> (see comparison-row-visibility).
              return (
                featureContent ? { ...featureContent, id: cover.id } : { id: cover.id }
              ) as IFeatureContent
            })
            return { ...policy, contents }
          })
          .filter(
            (policy) =>
              !shouldHideComparisonRow(
                filter,
                CompareBy.FEATURE,
                policy.contents || [],
                [],
                policy,
              ),
          )
        return { ...insurer, policies: policiesWithContents }
      })
    },
  ),
  getProductClasses: createSelector(getClassCategories, (classes: IClassLine[]) => {
    const allClasses =
      classes &&
      classes
        ?.filter((cl) => cl.name !== ClassLines.UNCATEGORISED)
        .map((cl) => cl.classes)
        .flat()
    return uniqBy(allClasses, 'id')
  }),
  getBuyerGroups: createSelector(
    (state: IRootState) => state?.comparison?.buyGroups,
    (buyGroups) => {
      const countryGroups = groupBy(buyGroups, 'country_code')
      const countries = Object.keys(countryGroups).map((key) => ({
        value: key,
        label: buyGroups.find((buyGroup: IBuyGroup) => buyGroup?.country_code === key)
          ?.country_name,
      }))
      return { countryGroups, countries }
    },
  ),

  // CSV Export Selectors
  getCSVExportData: createSelector(
    (state: IRootState) => state.comparison.filter,
    (state: IRootState) => state.comparison.selectedPolicyContents,
    (state: IRootState) => state.comparison.selectedSectionCovers,
    getFilteredGroupedFeaturesBySection,
    getFilteredGroupedPoliciesByInsurer,
    (
      filter,
      selectedPolicyContents: IFeatureContents[],
      selectedSectionCovers: IFeatureContents[],
      coverSections,
      policyFeatureContents,
    ) => {
      const csvData: any[] = []

      if (filter.compareBy === CompareBy.COMPANY) {
        // Export by Company (Feature-based comparison)
        coverSections.forEach((cover) => {
          cover.sections.forEach((section) => {
            section.features.forEach((feature) => {
              const rowContents = selectedPolicyContents.map((policyContent: IFeatureContents) => {
                const featureContent = policyContent.featureContents.find(
                  (fc) => fc.feature_id === feature.id,
                )
                return featureContent
                  ? { ...featureContent, id: policyContent.id }
                  : { id: policyContent.id }
              })

              if (
                shouldHideComparisonRow(
                  filter,
                  CompareBy.COMPANY,
                  rowContents,
                  selectedPolicyContents
                    .map((policyContent) => policyContent.policy)
                    .filter((policy): policy is IPolicy => Boolean(policy)),
                  undefined,
                )
              ) {
                return
              }

              const featureRow: any = {
                cover_name: cover.cover_name,
                section_name: section.section,
                feature_name: feature.name,
                feature_id: feature.id,
                is_key_selling_feature: feature.is_key_selling_feature || false,
                is_most_popular: feature.is_most_popular || false,
                is_water_perils: feature.is_water_perils || false,
                point_to_consider: feature.point_to_consider?.text || '',
              }

              // Add content for each selected policy
              selectedPolicyContents.forEach((policyContent: IFeatureContents, index) => {
                // Find the feature content for this specific policy and feature
                const featureContent = policyContent.featureContents.find(
                  (fc) => fc.feature_id === feature.id,
                )
                const policy = policyContent.policy

                featureRow[`policy_${index + 1}_name`] = policy?.name || ''
                featureRow[`policy_${index + 1}_code`] = policy?.code || ''
                featureRow[`policy_${index + 1}_insurer`] = policy?.insurer?.name || ''
                featureRow[`policy_${index + 1}_start_date`] = policy?.start_date || ''
                featureRow[`policy_${index + 1}_end_date`] = policy?.end_date || ''
                featureRow[`policy_${index + 1}_wording_path`] =
                  `${mediaUrl}/${policy?.wording_path}` || ''
                featureRow[`policy_${index + 1}_content`] = featureContent?.comment || ''
                featureRow[`policy_${index + 1}_icon`] = featureContent?.icon || ''
                featureRow[`policy_${index + 1}_page`] = featureContent?.page || ''
                featureRow[`policy_${index + 1}_is_new`] =
                  featureContent?.is_recently_updated || false
              })

              csvData.push(featureRow)
            })
          })
        })
      } else {
        // Export by Feature (Policy-based comparison)
        policyFeatureContents.forEach((insurer) => {
          insurer.policies.forEach((policy) => {
            const policyContents = selectedSectionCovers.map((sectionCover: IFeatureContents) => {
              const policyFeatureContent = sectionCover.featureContents.find(
                (content) => content.policy_proxy_id === policy.proxy_id,
              )
              return policyFeatureContent
                ? { ...policyFeatureContent, id: sectionCover.id }
                : { id: sectionCover.id }
            })

            if (shouldHideComparisonRow(filter, CompareBy.FEATURE, policyContents, [], policy)) {
              return
            }

            selectedSectionCovers.forEach((sectionCover: IFeatureContents) => {
              // For feature comparison, we need to get the feature content from the sectionCover
              sectionCover.featureContents.forEach((featureContent) => {
                // Find the corresponding content for this policy
                const policyFeatureContent = policy.contents?.find(
                  (c) => c.feature_id === featureContent.feature_id,
                )

                const policyRow: any = {
                  insurer_name: insurer.insurer,
                  insurer_rating: insurer.rating || '',
                  policy_name: policy.name,
                  policy_code: policy.code,
                  policy_start_date: policy.start_date,
                  policy_end_date: policy.end_date,
                  policy_wording_path: policy.wording_path,
                  policy_tmd_url: policy.tmd_url,
                  policy_is_expired: policy.is_expired,
                  policy_is_new: policy.is_new,
                  feature_name: featureContent.feature || '',
                  feature_id: featureContent.feature_id || '',
                  feature_section: featureContent.section || '',
                  feature_content: policyFeatureContent?.comment || featureContent.comment || '',
                  feature_icon: policyFeatureContent?.icon || featureContent.icon || '',
                  feature_page: policyFeatureContent?.page || featureContent.page || '',
                  feature_is_new:
                    policyFeatureContent?.is_recently_updated ||
                    featureContent.is_recently_updated ||
                    false,
                  point_to_consider: '', // Not available in this context
                }

                csvData.push(policyRow)
              })
            })
          })
        })
      }

      return csvData
    },
  ),

  getCSVExportHeaders: createSelector(
    (state: IRootState) => state.comparison.filter,
    (state: IRootState) => state.comparison.selectedPolicyContents,
    (filter, selectedPolicyContents: IFeatureContents[]) => {
      if (filter.compareBy === CompareBy.COMPANY) {
        // Headers for Company comparison
        const baseHeaders = [
          'Cover Name',
          'Section Name',
          'Feature Name',
          'Feature ID',
          'Key Selling Feature',
          'Most Popular',
          'Water Perils',
          'Point to Consider',
        ]

        const policyHeaders = selectedPolicyContents.flatMap((_, index) => [
          `Policy ${index + 1} Name`,
          `Policy ${index + 1} Code`,
          `Policy ${index + 1} Insurer`,
          `Policy ${index + 1} Start Date`,
          `Policy ${index + 1} End Date`,
          `Policy ${index + 1} Wording Path`,
          `Policy ${index + 1} Content`,
          `Policy ${index + 1} Icon`,
          `Policy ${index + 1} Page`,
          `Policy ${index + 1} Is New`,
        ])

        return [...baseHeaders, ...policyHeaders]
      } else {
        // Headers for Feature comparison
        return [
          'Insurer Name',
          'Insurer Rating',
          'Policy Name',
          'Policy Code',
          'Policy Start Date',
          'Policy End Date',
          'Policy Wording Path',
          'Policy TMD URL',
          'Policy Is Expired',
          'Policy Is New',
          'Feature Name',
          'Feature ID',
          'Feature Section',
          'Feature Content',
          'Feature Icon',
          'Feature Page',
          'Feature Is New',
          'Point to Consider',
        ]
      }
    },
  ),

  getCSVPolicyDetails: createSelector(
    (state: IRootState) => state.comparison.selectedPolicyContents,
    (selectedPolicyContents: IFeatureContents[]) => {
      return selectedPolicyContents.map((policyContent) => {
        const policy = policyContent.policy
        return {
          policy_id: policy?.id,
          policy_name: policy?.name,
          policy_code: policy?.code,
          policy_proxy_id: policy?.proxy_id,
          insurer_name: policy?.insurer?.name,
          insurer_website: policy?.insurer?.website,
          insurer_logo: policy?.insurer?.logo,
          underwriter_name: policy?.underwriter?.name,
          underwriter_csr: policy?.underwriter?.csr,
          start_date: policy?.start_date,
          end_date: policy?.end_date,
          wording_path: policy?.wording_path,
          tmd_url: policy?.tmd_url,
          is_expired: policy?.is_expired,
          is_archived: policy?.is_archived,
          is_pending: policy?.is_pending,
          is_new: policy?.is_new,
          is_express_cover: policy?.is_express_cover,
          cover_ids: policy?.cover_ids,
        }
      })
    },
  ),

  getCSVFeatureWordings: createSelector(
    getFilteredGroupedFeaturesBySection,
    (state: IRootState) => state.comparison.selectedPolicyContents,
    (coverSections, selectedPolicyContents: IFeatureContents[]) => {
      const wordingsData: any[] = []

      coverSections.forEach((cover) => {
        cover.sections.forEach((section) => {
          section.features.forEach((feature) => {
            selectedPolicyContents.forEach((policyContent: IFeatureContents) => {
              const content = feature.contents?.find(
                (c) => c.feature_id === feature.id && c.id === policyContent.id,
              )

              if (content?.comment) {
                wordingsData.push({
                  feature_id: feature.id,
                  feature_name: feature.name,
                  policy_id: policyContent.policy?.id,
                  policy_name: policyContent.policy?.name,
                  policy_code: policyContent.policy?.code,
                  insurer_name: policyContent.policy?.insurer?.name,
                  cover_name: cover.cover_name,
                  section_name: section.section,
                  wording_text: content.comment,
                  page_reference: content.page,
                  icon: content.icon,
                  is_recently_updated: content.is_recently_updated,
                })
              }
            })
          })
        })
      })

      return wordingsData
    },
  ),

  getCSVSectionSummary: createSelector(getFilteredGroupedFeaturesBySection, (coverSections) => {
    const sectionData: any[] = []

    coverSections.forEach((cover) => {
      cover.sections.forEach((section) => {
        sectionData.push({
          cover_id: cover.cover_id,
          cover_name: cover.cover_name,
          section_name: section.section,
          section_sequence: section.seq,
          feature_count: section.features.length,
          key_selling_features_count: section.features.filter((f) => f.is_key_selling_feature)
            .length,
          most_popular_features_count: section.features.filter((f) => f.is_most_popular).length,
          water_perils_features_count: section.features.filter((f) => f.is_water_perils).length,
        })
      })
    })

    return sectionData
  }),

  getCSVIconMapping: createSelector(
    getFilteredGroupedFeaturesBySection,
    (state: IRootState) => state.comparison.selectedPolicyContents,
    (coverSections, selectedPolicyContents: IFeatureContents[]) => {
      const iconData: any[] = []

      coverSections.forEach((cover) => {
        cover.sections.forEach((section) => {
          section.features.forEach((feature) => {
            selectedPolicyContents.forEach((policyContent: IFeatureContents) => {
              const content = feature.contents?.find(
                (c) => c.feature_id === feature.id && c.id === policyContent.id,
              )

              if (content?.icon) {
                iconData.push({
                  feature_id: feature.id,
                  feature_name: feature.name,
                  policy_id: policyContent.policy?.id,
                  policy_name: policyContent.policy?.name,
                  insurer_name: policyContent.policy?.insurer?.name,
                  icon_value: content.icon,
                  cover_name: cover.cover_name,
                  section_name: section.section,
                })
              }
            })
          })
        })
      })

      return iconData
    },
  ),
}
